/**
 * Budget-cap banner (#19 AC #8) — global MUI `<Alert>` shown on every
 * `/ai/*` page when the API reports the monthly budget is exceeded with
 * no active override.
 *
 * Behaviour:
 *   - Polls `useAiBudgetStatus()` every 30s (TanStack Query handles the
 *     interval; pauses when the tab is hidden).
 *   - Hidden when:
 *       (a) no data yet (loading),
 *       (b) `exceeded === false`, or
 *       (c) `overrideExpiresAt` is non-null (override active — banner
 *           collapses to a small note).
 *   - Visible when `exceeded && !overrideExpiresAt`. The "Start override
 *     window" button opens a small dialog with `1 hour` / `24 hours`
 *     choice → calls `POST /override-budget`. After success, the
 *     mutation invalidates `ai.all` so the next budget-status poll
 *     reflects the new state.
 *
 * The component does NOT use Tailwind, CSS modules, or raw CSS — pure
 * MUI per `standards/react-patterns.md`.
 */

import { useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useAiBudgetStatus, useStartBudgetOverride } from "../../hooks/useAiStudio";
import { ApiClientError } from "../../lib/api";
import { useToast } from "../../components/ToastProvider";

type OverrideHours = 1 | 24;

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

function formatRemaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return "expiring";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min remaining`;
  const hours = Math.round(minutes / 60);
  return `${hours} h remaining`;
}

export function BudgetBanner(): ReactElement | null {
  const status = useAiBudgetStatus();
  const startOverride = useStartBudgetOverride();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState<OverrideHours>(1);

  // The 429 case: the budget-status read endpoint is itself gated by the
  // budget guard (it shares the `/api/admin/ai` prefix). When the cap is
  // hit, the API responds 429 AI_BUDGET_EXCEEDED. We treat that error
  // exactly like an `exceeded: true` payload — the banner is meant to
  // surface the cap, and a 429 is the most authoritative signal we have.
  const erroredOverBudget =
    status.error instanceof ApiClientError && status.error.code === "AI_BUDGET_EXCEEDED";

  if (status.isPending) return null;

  // Override active — render a low-key info note instead of the warning.
  if (status.data?.overrideExpiresAt) {
    return (
      <Alert
        severity="info"
        icon={<WarningAmberIcon />}
        data-testid="ai-budget-banner-override-active"
        sx={{ mb: 2 }}
      >
        <AlertTitle>Budget override active</AlertTitle>
        <Typography variant="body2">
          Override window {formatRemaining(status.data.overrideExpiresAt)}. Generations continue to
          count against the monthly cap and will be blocked again once the override expires.
        </Typography>
      </Alert>
    );
  }

  // Not exceeded and no override — nothing to show.
  if (!erroredOverBudget && !status.data?.exceeded) return null;

  const spent = status.data?.spentUsd;
  const budget = status.data?.budgetUsd;

  const handleStart = () => {
    startOverride.mutate(
      { hours },
      {
        onSuccess: (data) => {
          toast.showSuccess(
            `Override window started (${data.hours}h). AI generations resume immediately.`,
          );
          setOpen(false);
        },
        onError: (err) => {
          toast.showError(err.message || "Could not start override window.");
        },
      },
    );
  };

  return (
    <>
      <Alert
        severity="warning"
        icon={<WarningAmberIcon />}
        data-testid="ai-budget-banner"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => setOpen(true)}
            data-testid="ai-budget-banner-start-override-button"
          >
            Start override window
          </Button>
        }
        sx={{ mb: 2 }}
      >
        <AlertTitle>Monthly AI budget reached</AlertTitle>
        <Typography variant="body2">
          {spent !== undefined && budget !== undefined
            ? `Spent ${formatUsd(spent)} of ${formatUsd(budget)} this month.`
            : "Monthly AI budget exceeded."}
          {" Generations are blocked until the override window starts or the next month begins."}
        </Typography>
      </Alert>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        data-testid="ai-budget-banner-override-dialog"
      >
        <DialogTitle>Start override window</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose how long the override should remain active. AI generations will resume
            immediately and continue to count against next month&apos;s cap.
          </DialogContentText>
          <Stack alignItems="center">
            <ToggleButtonGroup
              exclusive
              value={hours}
              onChange={(_e, value: OverrideHours | null) => {
                if (value !== null) setHours(value);
              }}
              aria-label="Override duration"
            >
              <ToggleButton value={1} data-testid="ai-budget-banner-override-1h">
                <Box>
                  <Typography variant="subtitle2">1 hour</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Quick fix
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value={24} data-testid="ai-budget-banner-override-24h">
                <Box>
                  <Typography variant="subtitle2">24 hours</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Whole day
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={startOverride.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStart}
            disabled={startOverride.isPending}
            data-testid="ai-budget-banner-override-confirm-button"
          >
            Start {hours}h window
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
